import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Field, Input, Select, Textarea } from "../ui";
import type { PropDef } from "../core/schema";
import { COMPUTED_KEYS } from "../core/computed";
import type { FilterAst } from "../core/filter";
import { FilterField } from "../filters/FilterBuilder";
import type { FilterSources } from "../filters/fields";

/**
 * Widget config form, hand-rolled from a JSON-Schema subset (spec §9.7; `@rjsf` not adopted, see
 * VERIFIED.md). Supported: number, boolean, string (+enum), and `x-drang` hints:
 *   "prop"      → a PropDef picker (optionally `x-kinds`, `x-computed` to include computed keys)
 *   "group" / "sequence" / "task" → pickers from the doc
 *   "filter"    → a task filter: its summary and the builder (ADR 0020); JSON when no sources are given
 *   "columns"   → what a board's columns are: a choice property (`prop:<id>`), done or not (`done`), or groups
 *                 (`group:<id,id,…>`, none ticked = all groups); typed values from before stay readable
 * Anything else (objects, arrays, the filter AST) is edited as JSON with validation.
 */
export interface JsonSchema {
  type?: string;
  title?: string;
  description?: string;
  enum?: (string | number)[];
  minimum?: number;
  maximum?: number;
  properties?: Record<string, JsonSchema>;
  "x-drang"?: "prop" | "group" | "sequence" | "task" | "counter" | "color" | "columns" | "filter";
  "x-kinds"?: string[];
  "x-computed"?: boolean;
  "x-prefix"?: string;
  /** shown when nothing is set (colour fields) */
  default?: string;
}

export interface FormSources {
  propDefs: PropDef[];
  groups: Record<string, { title: string }>;
  sequences: Record<string, { title: string }>;
  counters: Record<string, { title: string }>;
  /** for filter fields: the builder's choices and preview; without it a filter is edited as JSON */
  filter?: FilterSources | null;
}

export function ConfigForm({
  schema,
  value,
  onChange,
  sources,
}: {
  schema: JsonSchema | undefined;
  value: Record<string, unknown>;
  onChange(next: Record<string, unknown>): void;
  sources: FormSources;
}) {
  const { t } = useTranslation();
  const props = schema?.properties ?? {};
  const keys = Object.keys(props);
  if (!keys.length) return <p className="text-[12.5px] text-neutral-500">{t("dashboards.noOptions")}</p>;
  const set = (k: string, v: unknown) => {
    const next = { ...value };
    if (v === undefined || v === "") delete next[k];
    else next[k] = v;
    onChange(next);
  };
  // label left of the control; yes/no options gathered in one row at the end, structured values
  // (JSON) full width at the bottom, so the popover stays short
  const kindOf = (k: string) => {
    const p = props[k];
    if (p.type === "boolean") return "bool";
    if (p["x-drang"] === "filter" && !sources.filter) return "json";
    if (p["x-drang"] || p.enum || ["number", "integer", "string"].includes(p.type ?? "")) return "row";
    return "json";
  };
  const field = (k: string) => (
    <FieldFor
      key={k}
      name={k}
      schema={props[k]}
      value={value[k]}
      onChange={(v) => set(k, v)}
      sources={sources}
    />
  );
  /** `xFit` beside the range `xMin`–`xMax`: "fit to data" sits on the range's row, not with the other options */
  const fitOf = (rangeKey: string) => {
    const m = /^(.*)(Min|From)$/.exec(rangeKey);
    const k = m ? `${m[1]}Fit` : "";
    return k && props[k]?.type === "boolean" ? k : null;
  };
  const fitKeys = new Set(keys.map((k) => fitOf(k)).filter((k): k is string => !!k));
  const bools = keys.filter((k) => kindOf(k) === "bool" && !fitKeys.has(k));
  const rowKeys = keys.filter((k) => kindOf(k) === "row");
  /** what can pair up: plain numbers with numbers, colours with colours */
  const pairKind = (k: string) =>
    props[k]?.["x-drang"] === "color"
      ? "color"
      : ["number", "integer"].includes(props[k]?.type ?? "") && !props[k]?.enum
        ? "number"
        : null;
  /** `xMin` → `xMax` (or `xColorMin` → `xColorMax`) of the same kind: one row; the partner is skipped */
  const pairOf = (k: string): string | "skip" | null => {
    const kind = pairKind(k);
    if (!kind) return null;
    const m = /^(.*)(Min|From)$/.exec(k);
    if (m) {
      const partner = m[1] + (m[2] === "Min" ? "Max" : "To");
      if (rowKeys.includes(partner) && pairKind(partner) === kind) return partner;
    }
    const n = /^(.*)(Max|To)$/.exec(k);
    if (n) {
      const partner = n[1] + (n[2] === "Max" ? "Min" : "From");
      if (rowKeys.includes(partner) && pairKind(partner) === kind) return "skip";
    }
    return null;
  };
  return (
    <div className="flex flex-col gap-2.5" data-testid="config-form">
      <div className="grid grid-cols-[minmax(6rem,auto)_minmax(0,1fr)] items-center gap-x-3 gap-y-2">
        {rowKeys.map((k) => {
          const pair = pairOf(k);
          if (pair === "skip") return null;
          if (!pair) return field(k);
          // a range on one row: "X from [ ]  X to [ ]  [x] fit to data"
          const fit = fitOf(k);
          return (
            <PairRow
              key={k}
              first={{ name: k, schema: props[k], value: value[k], onChange: (v) => set(k, v) }}
              second={{ name: pair, schema: props[pair], value: value[pair], onChange: (v) => set(pair, v) }}
              fit={
                fit
                  ? {
                      name: fit,
                      schema: props[fit],
                      value: value[fit],
                      onChange: (v) => set(fit, v || undefined),
                    }
                  : undefined
              }
            />
          );
        })}
      </div>
      {bools.length > 0 && <div className="flex flex-wrap gap-x-4 gap-y-1.5">{bools.map(field)}</div>}
      {keys.filter((k) => kindOf(k) === "json").map(field)}
    </div>
  );
}

interface Part {
  name: string;
  schema: JsonSchema;
  value: unknown;
  onChange(v: unknown): void;
}

/** Two number fields that form a range, on one row of the grid (with "fit to data" when the widget has it). */
function PairRow({ first, second, fit }: { first: Part; second: Part; fit?: Part }) {
  const fitted = Boolean(fit?.value);
  const { t } = useTranslation();
  const label = (p: Part) => (p.schema.title ? t(p.schema.title, { defaultValue: p.name }) : p.name);
  const input = (p: Part) =>
    p.schema["x-drang"] === "color" ? (
      <ColourInput
        id={`cfg-${p.name}`}
        label={label(p)}
        value={p.value}
        onChange={p.onChange}
        fallback={p.schema.default}
      />
    ) : (
      <Input
        compact
        id={`cfg-${p.name}`}
        type="number"
        className="w-24"
        aria-label={label(p)}
        min={p.schema.minimum}
        max={p.schema.maximum}
        disabled={fitted}
        value={typeof p.value === "number" ? p.value : ""}
        onChange={(e) => p.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
      />
    );
  return (
    <Row label={label(first)} htmlFor={`cfg-${first.name}`}>
      <div className="flex flex-wrap items-center gap-2">
        {input(first)}
        <label htmlFor={`cfg-${second.name}`} className="text-[12.5px] text-neutral-500">
          {label(second)}
        </label>
        {input(second)}
        {fit && (
          <label className="flex items-center gap-1.5 text-[12.5px]" data-testid={`cfg-${fit.name}`}>
            <Checkbox checked={fitted} onChange={(v) => fit.onChange(v)} label={label(fit)} />
            {label(fit)}
          </label>
        )}
      </div>
    </Row>
  );
}

/** A colour swatch that opens the system colour picker; empty means the widget's default colour. */
function ColourInput({
  id,
  label,
  value,
  onChange,
  fallback,
}: {
  id: string;
  label: string;
  value: unknown;
  onChange(v: unknown): void;
  fallback?: string;
}) {
  const { t } = useTranslation();
  const v = typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value) ? value : "";
  return (
    <span className="inline-flex items-center gap-1">
      <input
        id={id}
        type="color"
        aria-label={label}
        value={v || fallback || "#9aa5b1"}
        onChange={(e) => onChange(e.target.value)}
        className="h-[26px] w-10 cursor-pointer rounded-sm border border-neutral-300 bg-[var(--surface)] p-0.5 dark:border-neutral-700"
      />
      {v ? (
        <button
          type="button"
          className="text-[11px] text-neutral-500 hover:text-accent"
          onClick={() => onChange(undefined)}
        >
          {t("config.colourDefault")}
        </button>
      ) : (
        <span className="text-[11px] text-neutral-400">{t("config.colourIsDefault")}</span>
      )}
    </span>
  );
}

/**
 * What a board's columns are: one per value of a choice property, done or not, or one per group (ticked groups;
 * none ticked means every group). Stored as `prop:<id>`, `done` or `group:<id,id,…>`.
 */
function ColumnsField({
  id,
  value,
  onChange,
  sources,
}: {
  id: string;
  value: unknown;
  onChange(v: unknown): void;
  sources: FormSources;
}) {
  const { t } = useTranslation();
  const cur = typeof value === "string" && value ? value : "prop:priority";
  const choiceProps = sources.propDefs
    .filter((d) => d.kind === "enum" && !d.spec.hidden)
    .map((d) => ({ value: `prop:${d.id}`, label: t(`props.${d.id}`, { defaultValue: d.name }) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const isGroups = cur.startsWith("group:");
  const picked = isGroups ? cur.slice(6).split(",").filter(Boolean) : [];
  const selected = isGroups ? "group:" : cur;
  // a value typed by hand before this field existed, which matches none of the choices: kept and shown as such
  const known = selected === "done" || selected === "group:" || choiceProps.some((o) => o.value === selected);
  const groups = Object.entries(sources.groups)
    .map(([gid, g]) => ({ id: gid, title: g.title }))
    .sort((a, b) => a.title.localeCompare(b.title));
  const toggle = (gid: string, on: boolean) => {
    const next = on ? [...picked, gid] : picked.filter((x) => x !== gid);
    onChange(`group:${next.join(",")}`);
  };
  return (
    <div className="flex flex-col gap-1.5">
      <Select
        compact
        id={id}
        className="w-full"
        value={selected}
        onChange={(e) => onChange(e.target.value)}
        data-testid="cfg-columns"
      >
        {!known && <option value={selected}>{t("config.columnsUnknown", { name: selected })}</option>}
        <optgroup label={t("config.columnsByProperty")}>
          {choiceProps.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </optgroup>
        <option value="done">{t("config.columnsDone")}</option>
        <option value="group:">{t("config.columnsGroups")}</option>
      </Select>
      {isGroups && (
        <fieldset className="flex flex-col gap-1" data-testid="cfg-columns-groups">
          <legend className="mb-0.5 text-[12px] text-neutral-500">{t("config.columnsWhichGroups")}</legend>
          {groups.length === 0 && (
            <p className="text-[12px] text-neutral-500">{t("config.columnsNoGroups")}</p>
          )}
          {groups.map((g) => (
            <label key={g.id} className="flex items-center gap-2 text-[13px]">
              <Checkbox checked={picked.includes(g.id)} onChange={(on) => toggle(g.id, on)} label={g.title} />
              {g.title}
            </label>
          ))}
        </fieldset>
      )}
    </div>
  );
}

/** Label left, control right: the two cells of one row in the form's grid. */
function Row({
  label,
  htmlFor,
  top = false,
  children,
}: {
  label: string;
  htmlFor: string;
  /** a tall control: the label sits by its first line */
  top?: boolean;
  children: ReactNode;
}) {
  return (
    <>
      <label
        htmlFor={htmlFor}
        className={
          top
            ? "self-start truncate pt-1.5 text-[12.5px] text-neutral-500"
            : "truncate text-[12.5px] text-neutral-500"
        }
      >
        {label}
      </label>
      <div className="min-w-0">{children}</div>
    </>
  );
}

function FieldFor({
  name,
  schema,
  value,
  onChange,
  sources,
}: {
  name: string;
  schema: JsonSchema;
  value: unknown;
  onChange(v: unknown): void;
  sources: FormSources;
}) {
  const { t } = useTranslation();
  const label = schema.title ? t(schema.title, { defaultValue: name }) : name;
  const id = `cfg-${name}`;
  const hint = schema["x-drang"];
  const prefix = schema["x-prefix"] ?? "";
  if (hint === "filter" && sources.filter)
    return (
      <Row label={label} htmlFor={id} top>
        <FilterField
          id={id}
          value={value as FilterAst | undefined}
          onChange={(v) => onChange(v)}
          sources={sources.filter}
        />
      </Row>
    );
  if (hint === "columns")
    return (
      <Row label={label} htmlFor={id} top>
        <ColumnsField id={id} value={value} onChange={onChange} sources={sources} />
      </Row>
    );
  if (hint === "color")
    return (
      <Row label={label} htmlFor={id}>
        <ColourInput id={id} label={label} value={value} onChange={onChange} fallback={schema.default} />
      </Row>
    );
  if (hint) {
    let options: { value: string; label: string }[] = [];
    if (hint === "prop") {
      const kinds = schema["x-kinds"];
      options = sources.propDefs
        .filter((d) => !kinds || kinds.includes(d.kind))
        .map((d) => ({ value: d.id, label: d.name }));
      if (schema["x-computed"])
        options = [...options, ...COMPUTED_KEYS.map((c) => ({ value: c, label: `${c} ·` }))];
    } else if (hint === "group")
      options = Object.entries(sources.groups).map(([id, g]) => ({ value: id, label: g.title }));
    else if (hint === "sequence")
      options = Object.entries(sources.sequences).map(([id, g]) => ({ value: id, label: g.title }));
    else if (hint === "counter")
      options = Object.entries(sources.counters).map(([id, g]) => ({ value: id, label: g.title }));
    const cur = typeof value === "string" && value.startsWith(prefix) ? value.slice(prefix.length) : "";
    return (
      <Row label={label} htmlFor={id}>
        <Select
          compact
          id={id}
          className="w-full"
          value={cur}
          onChange={(e) => onChange(e.target.value ? prefix + e.target.value : undefined)}
        >
          <option value="">—</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </Row>
    );
  }
  if (schema.enum) {
    return (
      <Row label={label} htmlFor={id}>
        <Select
          compact
          id={id}
          className="w-full"
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value || undefined)}
        >
          <option value="">—</option>
          {schema.enum.map((o) => (
            <option key={String(o)} value={String(o)}>
              {t(`${schema.title}_${o}`, { defaultValue: String(o) })}
            </option>
          ))}
        </Select>
      </Row>
    );
  }
  if (schema.type === "boolean") {
    return (
      <label className="flex items-center gap-2 text-[13px]">
        <Checkbox checked={Boolean(value)} onChange={(v) => onChange(v)} label={label} />
        {label}
      </label>
    );
  }
  if (schema.type === "number" || schema.type === "integer") {
    return (
      <Row label={label} htmlFor={id}>
        <Input
          compact
          id={id}
          type="number"
          className="w-24"
          min={schema.minimum}
          max={schema.maximum}
          value={typeof value === "number" ? value : ""}
          onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
        />
      </Row>
    );
  }
  if (schema.type === "string") {
    return (
      <Row label={label} htmlFor={id}>
        <Input
          compact
          id={id}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      </Row>
    );
  }
  return (
    <JsonField
      id={id}
      label={label}
      value={value}
      onChange={onChange}
      hint={schema.description ? t(schema.description, { defaultValue: "" }) : undefined}
    />
  );
}

function JsonField({
  id,
  label,
  value,
  onChange,
  hint,
}: {
  id: string;
  label: string;
  value: unknown;
  onChange(v: unknown): void;
  hint?: string;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(value === undefined ? "" : JSON.stringify(value));
  const [bad, setBad] = useState(false);
  return (
    <Field
      label={label}
      htmlFor={id}
      hint={bad ? <span className="text-err">{t("dashboards.badJson")}</span> : hint}
    >
      <Textarea
        id={id}
        rows={2}
        className={`font-mono text-[12px] ${bad ? "border-err" : ""}`}
        value={text}
        spellCheck={false}
        onChange={(e) => {
          setText(e.target.value);
          if (!e.target.value.trim()) {
            setBad(false);
            onChange(undefined);
            return;
          }
          try {
            onChange(JSON.parse(e.target.value));
            setBad(false);
          } catch {
            setBad(true);
          }
        }}
      />
    </Field>
  );
}
